import { z } from "zod";
import {
  SocialMediaKindSchema,
  SocialPlatformSchema,
  SocialPostKindSchema,
} from "../../enums/social";

const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);

export const SocialEngagementSchema = z
  .object({
    likes: z.number().int().min(0).nullable(),
    comments: z.number().int().min(0).nullable(),
    shares: z.number().int().min(0).nullable(),
    views: z.number().int().min(0).nullable(),
  })
  .strict();

export const NormalizedSocialPostSchema = z
  .object({
    platform: SocialPlatformSchema,
    externalPostId: z.string().min(1),
    providerAccountId: z.string().min(1).nullable(),
    authorHandle: z.string().min(1),
    authorDisplayName: z.string().min(1).nullable(),
    canonicalUrl: z.url(),
    kind: SocialPostKindSchema,
    text: z.string(),
    publishedAt: z.iso.datetime(),
    engagement: SocialEngagementSchema,
    providerRecordSha256: Sha256Schema,
  })
  .strict();
export type NormalizedSocialPost = z.infer<typeof NormalizedSocialPostSchema>;

export const SocialCaptureUploadAssetSchema = z
  .object({
    fieldName: z.string().regex(/^asset-[0-9]+$/),
    position: z.number().int().min(0),
    kind: SocialMediaKindSchema,
    contentType: z.string().min(1),
    sourceUrl: z.url(),
    sha256: Sha256Schema,
    byteLength: z.number().int().positive(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    durationMs: z.number().int().min(0).nullable(),
    altText: z.string().nullable(),
  })
  .strict();
export type SocialCaptureUploadAsset = z.infer<
  typeof SocialCaptureUploadAssetSchema
>;

export const SocialCaptureUploadMetadataSchema = z
  .object({
    post: NormalizedSocialPostSchema,
    assets: z.array(SocialCaptureUploadAssetSchema),
  })
  .strict();
export type SocialCaptureUploadMetadata = z.infer<
  typeof SocialCaptureUploadMetadataSchema
>;

export const SocialStoredAssetSchema = SocialCaptureUploadAssetSchema.omit({
  fieldName: true,
}).extend({
  id: z.uuid(),
  objectKey: z.string().min(1),
  evidenceKey: z.string().min(1),
});
export type SocialStoredAsset = z.infer<typeof SocialStoredAssetSchema>;

export const SocialCaptureManifestSchema = z
  .object({
    version: z.literal(1),
    captureId: z.uuid(),
    clientId: z.uuid(),
    profileId: z.uuid(),
    extractionAttemptId: z.uuid(),
    bundleSha256: Sha256Schema,
    postObjectKey: z.string().min(1),
    manifestObjectKey: z.string().min(1),
    post: NormalizedSocialPostSchema,
    assets: z.array(SocialStoredAssetSchema),
    capturedAt: z.iso.datetime(),
  })
  .strict();
export type SocialCaptureManifest = z.infer<typeof SocialCaptureManifestSchema>;

export const SocialCaptureUploadResponseSchema = z.object({
  captureId: z.uuid(),
  analysisAttemptId: z.uuid(),
  duplicate: z.boolean(),
});
export type SocialCaptureUploadResponse = z.infer<
  typeof SocialCaptureUploadResponseSchema
>;
